import axios from 'axios';
import {
  SET_SESSION,
  CLEAR_SESSION,
  REFRESH_SESSION,
  REFRESH_SESSION_FAILED,
  REFRESH_SESSION_SUCCESS,
  RETRY_ACTION,
} from './types';
import { accessDenied, apiError, apiStart, apiEnd } from '../api/actions';
import * as CognitoAuthService from '../services/CognitoAuthService';

export function setSession(session) {
  return {
    type: SET_SESSION,
    payload: session,
  };
}

export function clearSession() {
  return {
    type: CLEAR_SESSION,
    payload: null,
  };
}

// Refresh the access token via the Cognito SDK (transparently uses the
// refresh token under the hood) and replay the action that triggered the
// 401. Falls back to clearSession if Cognito has no recoverable session.
//
// `action` is the original API action that failed with 401 — it carries the
// url/method/data/handlers needed to retry.
export function refreshSession(action) {
  return async (dispatch, getState) => {
    dispatch({ type: REFRESH_SESSION });
    try {
      const cognitoSession = await CognitoAuthService.getCurrentSession();
      if (!cognitoSession) {
        dispatch(onFailedRefresh(new Error('No active Cognito session')));
        return;
      }
      const accessToken = cognitoSession.getAccessToken().getJwtToken();
      const refreshToken = cognitoSession.getRefreshToken().getToken();
      const current = (getState().session && getState().session.current) || {};
      const newSession = { ...current, token: accessToken, refreshToken };
      dispatch(setSession(newSession));
      dispatch({ type: REFRESH_SESSION_SUCCESS, payload: newSession });
      if (action) {
        retryAction(dispatch, newSession, action);
      }
    } catch (err) {
      dispatch(onFailedRefresh(err));
    }
  };
}

function onFailedRefresh(error) {
  return (dispatch) => {
    dispatch(clearSession());
    dispatch({
      type: REFRESH_SESSION_FAILED,
      payload: error,
    });
  };
}

function retryAction(dispatch, session, action) {
  const { url, method, data, onSuccess, onFailure, carry, headers } = action.payload;
  const dataOrParams = ['GET', 'DELETE'].includes(method) ? 'params' : 'data';

  if (session) {
    axios.defaults.headers.common.Authorization = `Bearer ${session.token}`;
    if (session.tenant) {
      axios.defaults.headers.common['X-Tenant-Id'] = session.tenant;
    } else {
      delete axios.defaults.headers.common['X-Tenant-Id'];
    }
  }

  if (carry) {
    dispatch(apiStart(carry));
  }

  let requestHeaders = headers;
  if (requestHeaders == null) {
    requestHeaders = axios.defaults.headers.common;
  }

  axios
    .request({
      url,
      method,
      headers: requestHeaders,
      [dataOrParams]: data,
    })
    .then(({ data: respData }) => {
      dispatch(onSuccess(respData));
    })
    .catch((error) => {
      dispatch(apiError(error));
      dispatch(onFailure(error));

      if (error.response && error.response.status === 403) {
        dispatch(accessDenied(window.location.pathname));
      }
    })
    .finally(() => {
      if (carry) {
        dispatch(apiEnd(carry));
      }
    });

  dispatch({
    type: RETRY_ACTION,
    payload: action.payload,
  });
}
